import React, { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { ChatWidget, BackToTop } from '../components/ChatAndUtils';
import PhoneInput from '../components/PhoneInput';
import { AusweisUploadBox } from '../components/scan/AusweisUploadBox';
import { useLegalAgb, AgbVolltext, WiderrufVolltext, AGB_PDF_URL, WIDERRUF_PDF_URL } from '../components/LegalTexts';
import { Button } from '../components/ui/button';
import { PhoneCall, Siren, Euro, Loader2, CheckCircle2, AlertCircle, CreditCard } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fieldClass = 'w-full px-4 py-3 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm rounded-sm';
const labelClass = 'block text-[#1B3A5C] text-xs tracking-wider font-medium mb-2';
const checkLabel = (err) => `flex items-start gap-3 cursor-pointer p-3 rounded-sm border ${err ? 'border-red-500 bg-red-50' : 'border-transparent'}`;

const INITIAL = {
  firstName: '', lastName: '', email: '', phone: '', street: '', zip: '', city: '', mandanten_nr: '', aktenzeichen: '', behoerde_name: '',
  notfallArt: '', beschreibung: '',
  honorar_akzeptiert: false, agb: false, datenschutz: false, widerrufsbelehrung_gelesen: false, widerruf_verlangt_beginn: false,
};
const REQUIRED = ['firstName', 'lastName', 'email', 'phone', 'street', 'zip', 'city', 'notfallArt', 'honorar_akzeptiert', 'agb', 'datenschutz', 'widerrufsbelehrung_gelesen', 'widerruf_verlangt_beginn'];
const BESTAND_OPTIONAL = new Set(['email', 'street', 'zip', 'city']);
const BEHOERDE_SKIP = new Set(['honorar_akzeptiert', 'agb', 'widerrufsbelehrung_gelesen', 'widerruf_verlangt_beginn']);
const MODI = ['neu', 'bestand', 'behoerde'];

const Err = ({ msg, id }) => msg ? <p className="mt-1 text-xs text-red-600 flex items-center gap-1" data-testid={`error-${id}`}><AlertCircle className="w-3 h-3" />{msg}</p> : null;


const Notfallkontakt = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const AUSWEIS_TEXTS = { cta: t('notfallkontakt.aw_cta'), formats: t('notfallkontakt.aw_formats'), changeHint: t('notfallkontakt.aw_change'), optimizing: t('notfallkontakt.aw_optimizing'), optimizedCropped: t('notfallkontakt.aw_cropped'), optimizedNoCrop: t('notfallkontakt.aw_nocrop'), useOriginal: t('notfallkontakt.aw_original') };
  const legalAgb = useLegalAgb('de');
  const [info, setInfo] = useState(null);
  const { user, isAuthenticated } = useAuth();
  const [modus, setModus] = useState(isAuthenticated ? 'neu' : 'behoerde');
  useEffect(() => { if (!isAuthenticated) setModus('behoerde'); }, [isAuthenticated]);
  const bestand = modus === 'bestand';
  const behoerde = modus === 'behoerde';
  const kurz = bestand || behoerde;
  const [form, setForm] = useState({ ...INITIAL, email: user?.email || '' });
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [errors, setErrors] = useState({});
  const [showAgb, setShowAgb] = useState(false);
  const [showWiderruf, setShowWiderruf] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/contact/notfallkontakt/info`).then((r) => setInfo(r.data)).catch((e) => console.warn('[notfall] info', e?.message));
  }, []);

  const update = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e)); };

  const validate = () => {
    const e = {};
    REQUIRED.forEach((k) => {
      if (kurz && BESTAND_OPTIONAL.has(k)) return;
      if (behoerde && BEHOERDE_SKIP.has(k)) return;
      if (!form[k] || (typeof form[k] === 'string' && !form[k].trim())) e[k] = t(`notfallkontakt.e_${k}`);
    });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t('notfallkontakt.e_email');
    if (bestand && !form.mandanten_nr.trim() && !form.aktenzeichen.trim()) e.mandanten_nr = t('notfallkontakt.e_mandanten_or_az');
    if (form.mandanten_nr.trim() && !/^4\d{4}$/.test(form.mandanten_nr.trim())) e.mandanten_nr = t('notfallkontakt.e_mandanten_format');
    if (behoerde && !form.behoerde_name.trim()) e.behoerde_name = t('notfallkontakt.e_behoerde');
    if (!kurz && !front) e.ausweis_front = t('notfallkontakt.e_ausweis');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast({ title: t('notfallkontakt.t_check'), description: t('notfallkontakt.t_check_desc'), variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('modus', modus);
      Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === 'boolean' ? String(v) : v.trim()));
      fd.append('agb_version', legalAgb?.stand || '');
      fd.append('consented_at', new Date().toISOString());
      if (front) fd.append('ausweis_front', front);
      if (back) fd.append('ausweis_back', back);
      const { data } = await axios.post(`${API}/contact/notfallkontakt`, fd);
      navigate(`/notfallkontakt/zugang?nf=${data.notfall_id}${isAuthenticated ? '' : `&key=${encodeURIComponent(data.access_key)}`}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast({ title: t('notfallkontakt.t_error'), description: typeof d === 'string' ? d : t('notfallkontakt.t_error_desc'), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const hotline = info?.hotline;

  return (
    <div className="min-h-screen bg-white">
      <Seo title={t('notfallkontakt.seo_title')} description={t('notfallkontakt.seo_desc')} path="/notfallkontakt" noindex />
      <Header />
      <main className="pt-[150px]">
        <section className="bg-[#1B3A5C] text-white py-16 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10"><div className="absolute bottom-0 right-0 w-96 h-96 bg-[#c0392b] rounded-full blur-3xl"></div></div>
          <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-3 text-[#e0b56a] text-xs tracking-[0.3em] mb-4"><span className="w-12 h-px bg-[#b8893d]"></span>{t('notfallkontakt.eyebrow')}</div>
            <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight mb-4">{t('notfallkontakt.title')}</h1>
            <p className="text-white/80 leading-relaxed max-w-3xl">{t('notfallkontakt.hero_text')}</p>
          </div>
        </section>

        {(
          <>
            <section className="py-12 bg-white">
              <div className="max-w-5xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  { icon: Siren, title: t('notfallkontakt.card_urgent'), desc: t('notfallkontakt.card_urgent_desc') },
                  { icon: Euro, title: t('notfallkontakt.card_paid'), desc: hotline ? t('notfallkontakt.card_paid_desc', { pauschale: hotline.pauschale, stundensatz: hotline.stundensatz }) : t('notfallkontakt.card_paid_default') },
                  { icon: CreditCard, title: t('notfallkontakt.card_pay'), desc: t('notfallkontakt.card_pay_desc') },
                ].map((c) => (
                  <div key={c.title} className="flex items-start gap-4 p-5 bg-[#f5f8fa] border-l-4 border-[#b8893d]">
                    <div className="w-11 h-11 bg-[#1B3A5C] rounded-sm flex items-center justify-center flex-shrink-0"><c.icon className="w-5 h-5 text-white" /></div>
                    <div><div className="text-[#1B3A5C] font-medium text-sm tracking-wide">{c.title}</div><div className="text-[#34536b] text-xs mt-1 leading-relaxed">{c.desc}</div></div>
                  </div>
                ))}
              </div>
            </section>

            <section className="pb-20 bg-white">
              <form onSubmit={submit} noValidate className="max-w-5xl mx-auto px-6 lg:px-10 space-y-10" data-testid="notfall-form">
                <div className="bg-[#fbf7f0] border-l-4 border-[#c0392b] p-5" data-testid="notfall-behoerden-hinweis">
                  <p className="text-sm text-[#1B3A5C] leading-relaxed">
                    <strong>{t('notfallkontakt.behoerden_title')}</strong> {hotline?.behoerden_hinweis || t('notfallkontakt.behoerden_default')}{' '}
                    {t('notfallkontakt.behoerden_choose')}
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-2xl text-[#1B3A5C] mb-4">{t('notfallkontakt.step1')}</h2>
                  {!isAuthenticated && <p className="mb-4 text-sm text-[#34536b] bg-[#f5f8fa] border border-gray-200 p-3" data-testid="notfall-anmelde-hinweis">{t('notfallkontakt.ohne_login_hinweis', 'Ohne Anmeldung steht das Formular nur Behörden und Gerichten zur Verfügung. Mandanten melden sich bitte zuerst an.')}</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="notfall-modus">
                    {MODI.map((mk) => (
                      <button key={mk} type="button" onClick={() => { if (!isAuthenticated && mk !== 'behoerde') { navigate('/login?next=/notfallkontakt'); return; } setModus(mk); setErrors({}); }} data-testid={`notfall-modus-${mk}`}
                        className={`text-left p-5 border-2 rounded-sm transition-colors ${modus === mk ? 'border-[#b8893d] bg-[#fbf7f0]' : 'border-gray-200 hover:border-[#b8893d]/50'}`}>
                        <div className="flex items-center gap-2 text-[#1B3A5C] font-medium text-sm tracking-wide">
                          {modus === mk ? <CheckCircle2 className="w-4 h-4 text-[#b8893d]" /> : <span className="w-4 h-4 rounded-full border border-gray-300 inline-block" />} {t(`notfallkontakt.modus_${mk}`)}
                        </div>
                        <div className="text-xs text-[#34536b] mt-1 leading-relaxed">{t(`notfallkontakt.modus_${mk}_desc`)}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="font-serif text-2xl text-[#1B3A5C] mb-6">{t(behoerde ? 'notfallkontakt.step2_behoerde' : 'notfallkontakt.step2')}</h2>
                  {behoerde && (
                    <div className="bg-[#f0f7f2] border-l-4 border-green-700 p-4 mb-5 text-sm text-[#1B3A5C]" data-testid="notfall-behoerde-frei">
                      <strong>{t('notfallkontakt.frei_title')}</strong> {hotline?.behoerden_hinweis || t('notfallkontakt.behoerden_default')} {t('notfallkontakt.frei_after')}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {behoerde && (
                      <div className="md:col-span-2"><label className={labelClass}>{t('notfallkontakt.l_behoerde')}</label><input className={fieldClass} value={form.behoerde_name} onChange={(e) => update('behoerde_name', e.target.value)} placeholder={t('notfallkontakt.ph_behoerde')} data-testid="notfall-behoerde-name" /><Err msg={errors.behoerde_name} id="behoerde_name" /></div>
                    )}
                    {bestand && (<>
                      <div><label className={labelClass}>{t('notfallkontakt.l_mandanten_nr')}</label><input className={fieldClass} value={form.mandanten_nr} onChange={(e) => update('mandanten_nr', e.target.value)} placeholder={t('notfallkontakt.ph_mandanten_nr')} data-testid="notfall-mandanten-nr" /><Err msg={errors.mandanten_nr} id="mandanten_nr" /></div>
                      <div><label className={labelClass}>{t('notfallkontakt.l_aktenzeichen')}</label><input className={fieldClass} value={form.aktenzeichen} onChange={(e) => { update('aktenzeichen', e.target.value); setErrors((er) => ({ ...er, mandanten_nr: undefined })); }} placeholder={t('notfallkontakt.ph_aktenzeichen')} data-testid="notfall-aktenzeichen" /></div>
                    </>)}
                    <div><label className={labelClass}>{t('notfallkontakt.l_firstName')}</label><input className={fieldClass} value={form.firstName} onChange={(e) => update('firstName', e.target.value)} data-testid="notfall-firstName" /><Err msg={errors.firstName} id="firstName" /></div>
                    <div><label className={labelClass}>{t('notfallkontakt.l_lastName')}</label><input className={fieldClass} value={form.lastName} onChange={(e) => update('lastName', e.target.value)} data-testid="notfall-lastName" /><Err msg={errors.lastName} id="lastName" /></div>
                    <div><label className={labelClass}>{t('notfallkontakt.l_phone')}</label><PhoneInput value={form.phone} onChange={(v) => update('phone', v)} testId="notfall-phone" inputClassName="px-4 py-3 bg-white border-gray-200 focus:border-[#b8893d] focus:ring-2 focus:ring-[#b8893d]/20" /><Err msg={errors.phone} id="phone" /></div>
                    <div><label className={labelClass}>{t('notfallkontakt.l_email')} {kurz ? t('notfallkontakt.email_optional') : '*'}</label><input type="email" className={fieldClass} value={form.email} readOnly title="E-Mail-Adresse Ihres Kontos" data-testid="notfall-email" /><Err msg={errors.email} id="email" /></div>
                    {!kurz && (<>
                      <div className="md:col-span-2"><label className={labelClass}>{t('notfallkontakt.l_street')}</label><input className={fieldClass} value={form.street} onChange={(e) => update('street', e.target.value)} data-testid="notfall-street" /><Err msg={errors.street} id="street" /></div>
                      <div><label className={labelClass}>{t('notfallkontakt.l_zip')}</label><input className={fieldClass} value={form.zip} onChange={(e) => update('zip', e.target.value)} data-testid="notfall-zip" /><Err msg={errors.zip} id="zip" /></div>
                      <div><label className={labelClass}>{t('notfallkontakt.l_city')}</label><input className={fieldClass} value={form.city} onChange={(e) => update('city', e.target.value)} data-testid="notfall-city" /><Err msg={errors.city} id="city" /></div>
                    </>)}
                  </div>
                </div>

                <div>
                  <h2 className="font-serif text-2xl text-[#1B3A5C] mb-6">{t('notfallkontakt.step3')}</h2>
                  <div className="grid grid-cols-1 gap-5">
                    <div>
                      <label className={labelClass}>{t('notfallkontakt.l_art')}</label>
                      <select className={fieldClass} value={form.notfallArt} onChange={(e) => update('notfallArt', e.target.value)} data-testid="notfall-art">
                        <option value="">{t('notfallkontakt.ph_art')}</option>
                        {(info?.arten || []).map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                      <Err msg={errors.notfallArt} id="notfallArt" />
                    </div>
                    <div>
                      <label className={labelClass}>{t('notfallkontakt.l_beschreibung')}</label>
                      <textarea rows={4} className={fieldClass} value={form.beschreibung} onChange={(e) => update('beschreibung', e.target.value)} placeholder={t('notfallkontakt.ph_beschreibung')} data-testid="notfall-beschreibung" />
                    </div>
                  </div>
                </div>

                {!kurz && (
                <div>
                  <h2 className="font-serif text-2xl text-[#1B3A5C] mb-2">{t('notfallkontakt.step4')}</h2>
                  <p className="text-sm text-[#34536b] mb-6">{t('notfallkontakt.ausweis_text')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {[
                      { key: 'ausweis_front', label: t('notfallkontakt.ausweis_front'), file: front, setter: setFront, name: 'Personalausweis_Vorderseite.jpg' },
                      { key: 'ausweis_back', label: t('notfallkontakt.ausweis_back'), file: back, setter: setBack, name: 'Personalausweis_Rueckseite.jpg' },
                    ].map((u) => (
                      <div key={u.key}>
                        <AusweisUploadBox label={u.label} file={u.file} error={errors[u.key]} testId={`input-${u.key}`} optimize optimizedName={u.name}
                          onTooLarge={() => toast({ title: t('notfallkontakt.too_large'), description: t('notfallkontakt.too_large_desc'), variant: 'destructive' })}
                          onChange={(f) => { u.setter(f || null); setErrors((er) => (er[u.key] ? { ...er, [u.key]: undefined } : er)); }}
                          texts={AUSWEIS_TEXTS} />
                        <Err msg={errors[u.key]} id={u.key} />
                      </div>
                    ))}
                  </div>
                </div>
                )}

                <div>
                  <h2 className="font-serif text-2xl text-[#1B3A5C] mb-6">{kurz ? '4.' : '5.'} {t(behoerde ? 'notfallkontakt.step5_behoerde' : 'notfallkontakt.step5')}</h2>
                  {!behoerde && (
                  <div className="bg-[#fbf7f0] border-l-4 border-[#b8893d] p-5 mb-4" data-testid="notfall-honorar-box">
                    <div className="text-[11px] tracking-[0.28em] text-[#b8893d] uppercase mb-2">{t('notfallkontakt.honorar_title')}</div>
                    <ul className="text-sm text-[#34536b] leading-relaxed list-disc pl-5 space-y-1">
                      <li>{t('notfallkontakt.honorar_pauschale')} <strong>{hotline?.pauschale || t('notfallkontakt.honorar_pauschale_default')}</strong></li>
                      <li>{t('notfallkontakt.honorar_stunde')} <strong>{hotline?.stundensatz || t('notfallkontakt.honorar_stunde_default')}</strong></li>
                      <li>{hotline?.behoerden_hinweis || t('notfallkontakt.honorar_behoerden_default')}</li>
                    </ul>
                  </div>
                  )}
                  <div className="space-y-2">
                    {!behoerde && (<>
                    <label className={checkLabel(errors.honorar_akzeptiert)}>
                      <input type="checkbox" checked={form.honorar_akzeptiert} onChange={(e) => update('honorar_akzeptiert', e.target.checked)} className="mt-1 w-4 h-4 accent-[#b8893d]" data-testid="checkbox-honorar" />
                      <span className="text-sm text-[#34536b]"><Trans i18nKey="notfallkontakt.chk_honorar" components={{ b: <strong /> }} /></span>
                    </label>
                    <Err msg={errors.honorar_akzeptiert} id="honorar_akzeptiert" />

                    <label className={checkLabel(errors.agb)}>
                      <input type="checkbox" checked={form.agb} onChange={(e) => update('agb', e.target.checked)} className="mt-1 w-4 h-4 accent-[#b8893d]" data-testid="checkbox-agb" />
                      <span className="text-sm text-[#34536b]"><Trans i18nKey="notfallkontakt.chk_agb" components={{ l: <Link to="/agb" target="_blank" rel="noopener" className="text-[#b8893d] underline" /> }} /></span>
                    </label>
                    <Err msg={errors.agb} id="agb" />
                    <div className="pl-3 flex flex-wrap gap-x-4 gap-y-1">
                      <button type="button" onClick={() => setShowAgb((v) => !v)} data-testid="agb-volltext-toggle" className="text-xs tracking-[0.15em] uppercase text-[#b8893d] underline hover:text-[#8f6a2b]">{t(showAgb ? 'notfallkontakt.agb_hide' : 'notfallkontakt.agb_show')}</button>
                      <a href={AGB_PDF_URL} target="_blank" rel="noopener noreferrer" className="text-xs tracking-[0.15em] uppercase text-[#1B3A5C] underline hover:text-[#b8893d]">{t('notfallkontakt.agb_pdf')}</a>
                    </div>
                    {showAgb && <AgbVolltext agb={legalAgb} />}
                    </>)}

                    <label className={checkLabel(errors.datenschutz)}>
                      <input type="checkbox" checked={form.datenschutz} onChange={(e) => update('datenschutz', e.target.checked)} className="mt-1 w-4 h-4 accent-[#b8893d]" data-testid="checkbox-datenschutz" />
                      <span className="text-sm text-[#34536b]"><Trans i18nKey="notfallkontakt.chk_datenschutz" values={{ ausweis: kurz ? '' : t('notfallkontakt.datenschutz_ausweis') }} components={{ l: <Link to="/datenschutz" target="_blank" rel="noopener" className="text-[#b8893d] underline" /> }} /></span>
                    </label>
                    <Err msg={errors.datenschutz} id="datenschutz" />

                    {!behoerde && (
                    <div className="mt-6 pt-6 border-t border-[#e5d8b8]" data-testid="widerruf-section">
                      <div className="text-[11px] tracking-[0.28em] text-[#b8893d] uppercase mb-2">{t('notfallkontakt.widerruf_title')}</div>
                      <p className="text-sm text-[#34536b] leading-relaxed mb-3">{t('notfallkontakt.widerruf_text')}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
                        <button type="button" onClick={() => setShowWiderruf((v) => !v)} data-testid="widerruf-belehrung-toggle" className="text-xs tracking-[0.15em] uppercase text-[#b8893d] underline hover:text-[#8f6a2b]">{t(showWiderruf ? 'notfallkontakt.widerruf_hide' : 'notfallkontakt.widerruf_show')}</button>
                        <a href={WIDERRUF_PDF_URL} target="_blank" rel="noopener noreferrer" className="text-xs tracking-[0.15em] uppercase text-[#1B3A5C] underline hover:text-[#b8893d]">{t('notfallkontakt.widerruf_pdf')}</a>
                      </div>
                      {showWiderruf && <WiderrufVolltext agb={legalAgb} />}
                      <label className={checkLabel(errors.widerrufsbelehrung_gelesen)}>
                        <input type="checkbox" checked={form.widerrufsbelehrung_gelesen} onChange={(e) => update('widerrufsbelehrung_gelesen', e.target.checked)} className="mt-1 w-4 h-4 accent-[#b8893d]" data-testid="checkbox-widerruf-gelesen" />
                        <span className="text-sm text-[#34536b]"><Trans i18nKey="notfallkontakt.chk_widerruf" components={{ b: <strong /> }} /></span>
                      </label>
                      <Err msg={errors.widerrufsbelehrung_gelesen} id="widerrufsbelehrung_gelesen" />
                      <label className={checkLabel(errors.widerruf_verlangt_beginn)}>
                        <input type="checkbox" checked={form.widerruf_verlangt_beginn} onChange={(e) => update('widerruf_verlangt_beginn', e.target.checked)} className="mt-1 w-4 h-4 accent-[#b8893d]" data-testid="checkbox-widerruf-beginn" />
                        <span className="text-sm text-[#34536b]"><Trans i18nKey="notfallkontakt.chk_beginn" components={{ b: <strong /> }} /></span>
                      </label>
                      <Err msg={errors.widerruf_verlangt_beginn} id="widerruf_verlangt_beginn" />
                      {!form.widerruf_verlangt_beginn && (
                        <div className="mt-2 text-[12px] text-[#8a6d1a] bg-[#fff8e1] border-l-2 border-[#b8893d] px-3 py-2" data-testid="widerruf-warning">
                          <Trans i18nKey="notfallkontakt.beginn_warning" components={{ b: <strong /> }} />
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center gap-4">
                  <Button type="submit" disabled={submitting} data-testid="notfall-submit" className="bg-[#c0392b] hover:bg-[#a93226] text-white rounded-sm px-8 py-6 text-sm tracking-[0.2em] whitespace-normal h-auto">
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('notfallkontakt.submitting')}</> : <><PhoneCall className="w-4 h-4 mr-2" /> {t('notfallkontakt.submit')}</>}
                  </Button>
                  <p className="text-xs text-[#7a8a99]">{t(behoerde ? 'notfallkontakt.after_behoerde' : 'notfallkontakt.after_default')} {t('notfallkontakt.after_mail')}</p>
                </div>
              </form>
            </section>
          </>
        )}
      </main>
      <Footer />
      <ChatWidget />
      <BackToTop />
      <Toaster />
    </div>
  );
};

export default Notfallkontakt;
